import type { MediaImage, MediaSourceImage, PropertyRoomTypeWithImages } from '../ImagesVideo';
import { ROOM_IMAGE_LIMIT, getRoomKey } from '../utils';

export { getRoomKey };

export const EMPTY_IMAGE_PUBLIC_IDS: Array<string> = [];

export interface RoomImage {
  publicId: string;
  title?: string;
}

export type RoomImagesByKey = Record<string, Array<RoomImage>>;

function buildRoomImages(
  images: PropertyRoomTypeWithImages['images'],
  excludedImagePublicIds: Array<string> = [],
): Array<RoomImage> {
  const seenPublicIds = new Set<string>();
  const excludedPublicIds = new Set(excludedImagePublicIds);

  return (images ?? []).reduce<Array<RoomImage>>((roomImages, image: MediaSourceImage) => {
    const publicId = image.id_cloudinary_external;

    if (!publicId || seenPublicIds.has(publicId) || excludedPublicIds.has(publicId)) {
      return roomImages;
    }

    seenPublicIds.add(publicId);
    roomImages.push({
      publicId,
      title: image.title || undefined,
    });

    return roomImages;
  }, []);
}

export function buildRoomImagesByKey(
  propertyRoomTypes: Array<PropertyRoomTypeWithImages>,
  excludedImagePublicIds: Array<string>,
): RoomImagesByKey {
  return propertyRoomTypes.reduce<RoomImagesByKey>((roomImagesByKey, room, index) => {
    roomImagesByKey[getRoomKey(room, index)] = buildRoomImages(room.images, excludedImagePublicIds).slice(
      0,
      ROOM_IMAGE_LIMIT,
    );
    return roomImagesByKey;
  }, {});
}

export function pruneUnavailableRoomImages(
  roomImagesByKey: RoomImagesByKey,
  mediaImages: Array<MediaImage>,
): RoomImagesByKey {
  const availablePublicIds = new Set(mediaImages.map((image) => image.publicId));
  let hasChanged = false;

  const nextRoomImagesByKey = Object.fromEntries(
    Object.entries(roomImagesByKey).map(([roomKey, roomImages]) => [
      roomKey,
      roomImages.filter((image) => {
        const isAvailable = availablePublicIds.has(image.publicId);

        if (!isAvailable) {
          hasChanged = true;
        }

        return isAvailable;
      }),
    ]),
  );

  return hasChanged ? nextRoomImagesByKey : roomImagesByKey;
}
